<template>
  <div class="simple-tinymce-editor">
    <textarea ref="editorElement"></textarea>
  </div>
</template>

<script>
export default {
  name: 'TinyMceEditor',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Tulis konten di sini...'
    },
    height: {
      type: [Number, String],
      default: 300
    },
    initialContent: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      editor: null,
      isUpdating: false,
      editorId: `tinymce-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      isEditorReady: false,
      pendingContent: null,
      scriptElement: null,
      initTimeout: null,
      contentTimeout: null
    };
  },
  mounted() {
    this.initTinyMce();
  },
  beforeUnmount() {
    this.destroyEditor();
  },
  watch: {
    modelValue(newVal) {
      if (this.editor && !this.isUpdating && this.isEditorReady && newVal !== this.editor.getContent()) {
        this.editor.setContent(newVal || '');
      }
    },
    initialContent: {
      handler(content) {
        if (content && this.editor && this.isEditorReady) {
          this.editor.setContent(content);
        } else if (content) {
          this.pendingContent = content;
        }
      },
      immediate: true
    }
  },
  methods: {
    async initTinyMce() {
      if (window.tinymce) {
        this.setupEditor();
      } else {
        await this.loadTinyMCEScript();
      }
    },
    
    async loadTinyMCEScript() {
      return new Promise((resolve, reject) => {
        // Remove any existing script first
        if (this.scriptElement) {
          document.head.removeChild(this.scriptElement);
        }

        this.scriptElement = document.createElement('script');
        this.scriptElement.src = 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.4.2/tinymce.min.js';
        this.scriptElement.async = true;
        this.scriptElement.onload = () => {
          this.setupEditor();
          resolve();
        };
        this.scriptElement.onerror = (error) => {
          console.error('Failed to load TinyMCE:', error);
          reject(error);
        };
        document.head.appendChild(this.scriptElement);
      });
    },
    
    setupEditor() {
      const element = this.$refs.editorElement;
      if (!element) return;

      element.id = this.editorId;

      // Use setTimeout to ensure the DOM has been updated
      setTimeout(() => {
        window.tinymce.init({
          selector: `#${this.editorId}`,
          height: this.height,
          min_height: this.height, // Ensure minimum height
          menubar: false,
          plugins: ['directionality', 'lists', 'link'], // Removed 'autoresize'
          toolbar: 'undo redo | bold italic underline | bullist numlist | link | removeformat',
          directionality: 'ltr', // Ensure left-to-right text direction
          content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:16px }',
          placeholder: this.placeholder,
          branding: false,
          elementpath: false,
          statusbar: false,
          resize: false,
          promotion: false,
  
          setup: (editor) => {
            this.editor = editor;
  
            editor.on('init', () => {
              // Set editor as ready
              this.isEditorReady = true;
              
              // Priority: pendingContent > initialContent > modelValue
              const contentToSet = this.pendingContent || 
                               (this.initialContent !== null ? this.initialContent : this.modelValue) || 
                               '';
              
              // Safely set content with a slight delay to ensure editor is ready
              setTimeout(() => {
                editor.setContent(contentToSet);
                
                // Force the editor to update its undo state
                editor.undoManager.clear();
                editor.undoManager.add();
                
                // Emit the initial content if needed
                this.isUpdating = true;
                this.$emit('update:modelValue', editor.getContent());
                this.$nextTick(() => {
                  this.isUpdating = false;
                });
              }, 50);
              
              this.pendingContent = null;
            });
  
            editor.on('input change keyup', () => {
              this.isUpdating = true;
              const content = editor.getContent();
              this.$emit('update:modelValue', content);
              this.$nextTick(() => {
                this.isUpdating = false;
              });
            });
          }
        });
      }, 0);
    },
    
    destroyEditor() {
      if (this.editor) {
        try {
          // Remove all editor event listeners first
          this.editor.off();
          
          // Remove the editor from DOM
          this.editor.remove();
          
          // Destroy the editor instance
          this.editor.destroy(false); // false prevents automatic removal
          
          // Clean up the editor container
          const editorContainer = document.getElementById(this.editorId);
          if (editorContainer) {
            editorContainer.innerHTML = '';
          }
          
          // Remove TinyMCE instance from global registry
          if (window.tinymce && window.tinymce.editors) {
            delete window.tinymce.editors[this.editorId];
          }
        } catch (e) {
          console.error('Error destroying TinyMCE editor:', e);
        } finally {
          this.editor = null;
          this.isEditorReady = false;
        }
      }
      
      // Clean up script element
      if (this.scriptElement && document.head.contains(this.scriptElement)) {
        document.head.removeChild(this.scriptElement);
        this.scriptElement = null;
      }
      
      // Clear all timeouts
      clearTimeout(this.initTimeout);
      clearTimeout(this.contentTimeout);
      
      // Reset state
      this.pendingContent = null;
    }
  }
};
</script>

<style scoped>
.simple-tinymce-editor {
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}

/* Custom styling to make TinyMCE look more streamlined */
:deep(.tox-tinymce) {
  border: none !important;
}

:deep(.tox-toolbar__primary) {
  background-color: #f8f9fa !important;
}

:deep(.tox-toolbar-overlord) {
  border-bottom: 1px solid #ddd !important;
}

/* Simplify toolbar styling */
:deep(.tox-toolbar__group) {
  padding: 4px !important;
}

:deep(.tox-tbtn) {
  height: 32px !important;
  width: 32px !important;
  margin: 0 2px !important;
}

/* Simplify editor styling */
:deep(.tox-edit-area) {
  padding: 0 8px;
}

:deep(.tox-edit-area__iframe) {
  background-color: white;
}
</style>
